import { CircleHelp } from "lucide-react";
import { Checkbox, FormItem, Input, Select, Tabs, TagsInput, Tooltip } from "~/components/ui";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { compactFormItemStyle, type SchemaForm } from "~/utils/schema-admin";
import { TYPE_OPTIONS, VALIDATION_PHASE_OPTIONS } from "~/utils/schema-form-utils";
import { ExtractionSourceForm } from "./extraction-source-form";
import { HelpLabel } from "./help-label";
import { TemplateEditor } from "./template-editor";

interface SchemaFieldPanelProps {
  index: number;
  /** Focus the name input when the panel just opened on a fresh field. */
  autoFocusName: boolean;
}

/** General / extraction / display tabs for one field of the schema form. */
export function SchemaFieldPanel({ index, autoFocusName }: SchemaFieldPanelProps) {
  const { t } = useTranslation("admin");
  const {
    control,
    watch,
    formState: { errors },
  } = useFormContext<SchemaForm>();

  return (
    <Tabs
      size="small"
      items={[
        {
          key: "general",
          label: t("schemaAdmin.tabs.general"),
          children: (
            <div className="space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <FormItem
                  label={<HelpLabel label={t("schemaAdmin.field.name")} helpKey="schemaAdmin.help.fieldName" />}
                  className={compactFormItemStyle}
                  required
                  validateStatus={errors.fields?.[index]?.name ? "error" : ""}
                  help={errors.fields?.[index]?.name ? t("common:formValidations.required") : undefined}
                >
                  <Controller
                    name={`fields.${index}.name`}
                    control={control}
                    render={({ field }) => (
                      <Input
                        {...field}
                        placeholder="field_name"
                        className="font-mono"
                        autoFocus={autoFocusName && !field.value}
                      />
                    )}
                  />
                </FormItem>
                <FormItem
                  label={<HelpLabel label={t("schemaAdmin.field.type")} helpKey="schemaAdmin.help.fieldType" />}
                  className={compactFormItemStyle}
                >
                  <Controller
                    name={`fields.${index}.type`}
                    control={control}
                    render={({ field }) => (
                      <Select {...field} value={field.value ?? ""} options={TYPE_OPTIONS} className="w-full" />
                    )}
                  />
                </FormItem>
                <FormItem
                  label={
                    <HelpLabel
                      label={t("schemaAdmin.field.validationPhase")}
                      helpKey="schemaAdmin.help.validationPhase"
                    />
                  }
                  className={compactFormItemStyle}
                >
                  <Controller
                    name={`fields.${index}.validation_phase`}
                    control={control}
                    render={({ field }) => (
                      <Select
                        {...field}
                        value={field.value ?? ""}
                        options={VALIDATION_PHASE_OPTIONS}
                        className="w-full"
                      />
                    )}
                  />
                </FormItem>
                <FormItem label=" " className={compactFormItemStyle}>
                  <Controller
                    name={`fields.${index}.required`}
                    control={control}
                    render={({ field }) => (
                      <Checkbox checked={field.value} onChange={field.onChange}>
                        {t("schemaAdmin.field.required")}
                      </Checkbox>
                    )}
                  />
                </FormItem>
              </div>

              <FormItem
                label={
                  <HelpLabel label={t("schemaAdmin.field.description")} helpKey="schemaAdmin.help.fieldDescription" />
                }
                className={compactFormItemStyle}
              >
                <Controller
                  name={`fields.${index}.description`}
                  control={control}
                  render={({ field }) => (
                    <Input {...field} placeholder={t("schemaAdmin.placeholders.fieldDescription")} />
                  )}
                />
              </FormItem>

              {watch(`fields.${index}.type`) === "enum" && (
                <FormItem
                  label={
                    <HelpLabel label={t("schemaAdmin.field.allowedValues")} helpKey="schemaAdmin.help.allowedValues" />
                  }
                  className={compactFormItemStyle}
                >
                  <Controller
                    name={`fields.${index}.allowed_values`}
                    control={control}
                    render={({ field }) => (
                      <TagsInput
                        value={field.value ?? []}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        placeholder={t("schemaAdmin.field.allowedValuesPlaceholder")}
                        tokenSeparators={[","]}
                        className="w-full"
                      />
                    )}
                  />
                </FormItem>
              )}
            </div>
          ),
        },
        {
          key: "extraction",
          label: t("schemaAdmin.tabs.extraction"),
          children: <ExtractionSourceForm fieldIndex={index} />,
        },
        {
          key: "display",
          label: t("schemaAdmin.tabs.display"),
          children: (
            <div className="space-y-3">
              <div className="inline-flex items-center gap-2">
                <Tooltip title={t("schemaAdmin.help.filterable")}>
                  <span className="inline-flex">
                    <Controller
                      name={`fields.${index}.vectorstore.filterable`}
                      control={control}
                      render={({ field }) => (
                        <Checkbox checked={field.value} onChange={field.onChange}>
                          {t("schemaAdmin.field.filterable")} <CircleHelp size={14} className="text-noa-muted" />
                        </Checkbox>
                      )}
                    />
                  </span>
                </Tooltip>
                <Tooltip title={t("schemaAdmin.help.searchable")}>
                  <span className="inline-flex">
                    <Controller
                      name={`fields.${index}.vectorstore.searchable`}
                      control={control}
                      render={({ field }) => (
                        <Checkbox checked={field.value} onChange={field.onChange}>
                          {t("schemaAdmin.field.searchable")} <CircleHelp size={14} className="text-noa-muted" />
                        </Checkbox>
                      )}
                    />
                  </span>
                </Tooltip>
              </div>
              <FormItem
                label={
                  <HelpLabel label={t("schemaAdmin.field.promptTemplate")} helpKey="schemaAdmin.help.promptTemplate" />
                }
                className={compactFormItemStyle}
              >
                <Controller
                  name={`fields.${index}.vectorstore.prompt_template`}
                  control={control}
                  render={({ field }) => <TemplateEditor value={field.value} onChange={field.onChange} />}
                />
              </FormItem>
              <FormItem
                label={
                  <HelpLabel
                    label={t("schemaAdmin.field.embeddingTemplate")}
                    helpKey="schemaAdmin.help.embeddingTemplate"
                  />
                }
                className={compactFormItemStyle}
              >
                <Controller
                  name={`fields.${index}.vectorstore.embedding_template`}
                  control={control}
                  render={({ field }) => (
                    <TemplateEditor value={field.value} onChange={field.onChange} placeholder="Équipement: {value}" />
                  )}
                />
              </FormItem>
            </div>
          ),
        },
      ]}
    />
  );
}
